import React, { forwardRef, useEffect, useState } from 'react';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import Banner from '@rover/kibble/official-patterns/Banner';

import announceToast from './liveAnnouncer';
import {
  DEFAULT_AUTO_HIDE_DURATION,
  DEFAULT_PLACEMENT,
  Props,
  TOAST_EDGE_OFFSET,
  TOAST_FONT_SIZE,
  TOAST_MAX_WIDTH,
  TOAST_WIDTH,
  ToastHandle,
  ToastPlacement,
} from './Toast.common';
import ToastFooterLink from './ToastFooterLink';
import useToastDismissal from './useToastDismissal';

type BannerContainerProps = NonNullable<Props['containerProps']>;

/**
 * Box props pinning the toast to its edge; `inline` and stacked toasts contribute none. A bare
 * Toast does not portal, so an ancestor `transform` or `filter` will capture its `fixed`.
 */
const getPlacementProps = (placement: ToastPlacement, isStacked: boolean): BannerContainerProps => {
  if (placement === 'inline' || isStacked) return {};

  return {
    position: 'fixed',
    left: TOAST_EDGE_OFFSET,
    right: TOAST_EDGE_OFFSET,
    maxWidth: TOAST_MAX_WIDTH,
    zIndex: ZIndex.TOAST.toString(),
    ...(placement === 'top' ? { top: TOAST_EDGE_OFFSET } : { bottom: TOAST_EDGE_OFFSET }),
  };
};

/**
 * Banner's own region is muted when `liveAnnouncer` has the message, to stop it double-speaking.
 * A JSX body has no text to hand over, so it falls back to an `alert` region on the toast.
 */
const getAnnouncementProps = (
  isAnnouncedElsewhere: boolean,
  announce: boolean
): { 'aria-live': 'assertive' | 'off'; webProps: BannerContainerProps['webProps'] } => {
  if (!announce || isAnnouncedElsewhere) {
    return { 'aria-live': 'off', webProps: { role: undefined } };
  }

  return { 'aria-live': 'assertive', webProps: { role: 'alert' } };
};

const getHiddenTransform = (placement: ToastPlacement): string | undefined => {
  if (placement === 'inline') return undefined;
  return placement === 'top' ? 'translateY(-100%)' : 'translateY(100%)';
};

/** A brief message that floats over the page and goes away on its own. */
const Toast = forwardRef(function Toast(
  {
    webProps = {},
    // pulled out and dropped, because this is the web implementation
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    rxnProps,
    ...props
  }: Props,
  ref: React.Ref<ToastHandle>
): JSX.Element {
  // The web-specific overrides win over their top-level twins.
  const { closeButtonRef, ...webOverrides } = webProps;
  const {
    autoHideDuration = DEFAULT_AUTO_HIDE_DURATION,
    placement = DEFAULT_PLACEMENT,
    announce = true,
    isStacked = false,
    isPaused = false,
    timerEpoch = 0,
    link,
    onClose,
    onExitStart,
    ...bannerProps
  } = { ...props, ...webOverrides };
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);

  const { isShown, animationDuration, handleDismissPress } = useToastDismissal({
    ref,
    autoHideDuration,
    isPaused: isPaused || isHovered || isFocusWithin,
    timerEpoch,
    onClose,
    onExitStart,
  });

  // Narrowed outside the effect, so a JSX body's per-render identity cannot re-speak it.
  const announcement =
    announce && typeof bannerProps.children === 'string' ? bannerProps.children : undefined;

  // Announced exactly once, on entry, so no later prop change can repeat it.
  useEffect(() => {
    if (!announcement) return undefined;
    return announceToast(announcement);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on entry
  }, []);

  const handleBlur = (event: React.FocusEvent<HTMLElement>): void => {
    // Focus moving between two elements inside the toast is still focus-within.
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setIsFocusWithin(false);
  };

  const hiddenTransform = getHiddenTransform(placement);
  const announcementProps = getAnnouncementProps(announcement !== undefined, announce);
  const callerContainerProps = bannerProps.containerProps;

  return (
    <Banner
      {...bannerProps}
      onClose={handleDismissPress}
      FooterComponent={
        link ? <ToastFooterLink link={link} onPressed={handleDismissPress} /> : undefined
      }
      webProps={{ closeButtonRef }}
      containerProps={{
        ...getPlacementProps(placement, isStacked),
        ...announcementProps,
        ...callerContainerProps,
        webProps: {
          ...announcementProps.webProps,
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
          onFocus: () => setIsFocusWithin(true),
          onBlur: handleBlur,
          ...callerContainerProps?.webProps,
        },
        sx: {
          transition: `transform ${animationDuration}ms ease, opacity ${animationDuration}ms ease`,
          boxShadow: 'elevation_low',
          opacity: isShown ? 1 : 0,
          pointerEvents: isShown ? undefined : 'none',
          // A pinned toast is sized by its insets: an explicit width cancels the auto margins.
          ...(placement === 'inline' || isStacked ? { width: TOAST_WIDTH } : { mx: 'auto' }),
          ...(placement === 'inline'
            ? {}
            : { transform: isShown ? 'translateY(0)' : hiddenTransform }),
          '@media (prefers-reduced-motion: reduce)': {
            transform: 'none',
            transition: `opacity ${animationDuration}ms ease`,
          },
          ...callerContainerProps?.sx,
        },
      }}
      fontSize={TOAST_FONT_SIZE}
    />
  );
});

export default Toast;
